import './styles/ModuleItem.scss';

import { useApolloClient, useMutation } from '@apollo/client';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DoDisturbAltIcon from '@mui/icons-material/DoDisturbAlt';
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  Box,
  Divider,
  Fab,
  Grid,
  Icon,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Tooltip,
  Typography
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import MUTATION from 'gql/mutations/ModulesService/deleteModule.gql';
import UPDATE_STATE from 'gql/mutations/ModulesService/updateModule.gql';
import UPDATE_TAKING_PLACE from 'gql/mutations/ModulesService/updateTakingPlace.gql';
import DEFAULT_MODULES_QUERY from 'gql/queries/ModulesService/getDefaultModules.gql';
import USER_QUERY from 'gql/queries/currentUser.gql';

import { useCoursesQuery } from 'api/hooks/useCoursesQuery';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import ContextMenu from 'app/components/ContextMenu';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { Modal } from 'app/components/Modal';
import { hasAtLeastStarted } from 'app/containers/AWPM-FWPM/ModuleSelections/helper';
import { ModuleSelection, SubjectType } from 'app/containers/AWPM-FWPM/ModuleSelections/models';
import ModuleModal from 'app/containers/AWPM-FWPM/Modules/ModuleModal';
import { ModuleWithComplete } from 'app/containers/AWPM-FWPM/Modules/models';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { Course } from 'types/AppTypes';
import { ACLHelper } from 'utils/ACLHelper';
import { excludeCoursesFromAllCourses } from 'utils/excludeCoursesHelper';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';

import { LabeledInput } from '../../CompanyItem/components/LabeledInput';
import { ColouredInputBox } from './components/ColouredInputBox';
import { CourseChip } from './components/CourseChip';
import { getModuleLecturer, isModuleActive } from './helper';

interface Props {
  module: ModuleWithComplete;
  deactivateButtons?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  isPreview?: boolean;
  deactiveLink?: boolean;
  onDelete?: () => void;
  onEdit?: () => void;
  subjectType: SubjectType;
  inStudentView?: boolean;
  checked?: boolean;
  disabled?: boolean;
  selection?: ModuleSelection;
}

interface ModuleControlButtonProps {
  menuOpen: boolean;
  anchorRef: React.MutableRefObject<null>;
  setEditModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDeleteModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setChangeStateModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsNotTakingPlaceModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleChangeIsTakingPlace: Function;
  isTakingPlace?: boolean;
  isActive?: boolean;
  isDefault?: boolean;
  selection?: ModuleSelection;
}

export enum States {
  Draft = 'draft',
  Incomplete = 'incomplete',
  Complete = 'complete',
  TookPlace = 'tookPlace',
  IsNotTakingPlace = 'isNotTakingPlace'
}
export enum ActiveState {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE'
}

const ControlButtons = ({
  menuOpen,
  setMenuOpen,
  anchorRef,
  setEditModalOpen,
  setDeleteModalOpen,
  setChangeStateModalOpen,
  setIsNotTakingPlaceModalOpen,
  handleChangeIsTakingPlace,
  isTakingPlace,
  isActive,
  isDefault,
  selection
}: ModuleControlButtonProps) => {
  const { t } = useTranslation();
  return (
    <ContextMenu
      className="module__header__controls"
      open={menuOpen}
      setOpen={setMenuOpen}
      anchorEl={anchorRef.current}
    >
      <MenuItem className="module__header__controls__container">
        <Grid
          container
          className="module__controls__container"
          onClick={e => {
            setEditModalOpen(true);
            setMenuOpen(false);
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <Grid className="module__header__controls__container__icon" item xs={3}>
            <ListItemIcon>
              <Icon
                className="fas fa-pen fa-sm"
                style={{
                  width: 'auto',
                  color: 'lightskyblue',
                  fontSize: '1.1em'
                }}
              />
            </ListItemIcon>
          </Grid>
          <Grid className="module__header__controls__container" item xs={9}>
            <ListItemText primary={t(translations.common.edit())} />
          </Grid>
        </Grid>
      </MenuItem>
      {isDefault ? (
        <MenuItem className="module__header__controls__container">
          {isActive ? (
            <Grid
              container
              className="module__header__controls__container"
              onClick={e => {
                setChangeStateModalOpen(true);
                setMenuOpen(false);
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <Grid className="module__header__controls__container__icon" item xs={3}>
                <ListItemIcon>
                  <DoDisturbAltIcon
                    style={{
                      width: 'auto',
                      color: 'grey',
                      fontSize: '1.1em'
                    }}
                  />
                </ListItemIcon>
              </Grid>
              <Grid item xs={9}>
                <ListItemText primary={t(translations.pages.modules.deactivate())} />
              </Grid>
            </Grid>
          ) : (
            <Grid
              container
              className="module__header__controls__container"
              onClick={e => {
                setChangeStateModalOpen(true);
                setMenuOpen(false);
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <Grid className="module__header__controls__container__icon" item xs={3}>
                <ListItemIcon>
                  <RadioButtonCheckedIcon
                    style={{
                      width: 'auto',
                      color: 'green',
                      fontSize: '1.1em'
                    }}
                  />
                </ListItemIcon>
              </Grid>
              <Grid item xs={9}>
                <ListItemText primary={t(translations.pages.modules.activate())} />
              </Grid>
            </Grid>
          )}
        </MenuItem>
      ) : !!isTakingPlace ? (
        <MenuItem className="module__header__contorls__container">
          <Grid
            container
            className="module__header__controls__container"
            onClick={e => {
              setIsNotTakingPlaceModalOpen(true);
              setMenuOpen(false);
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <Grid className="module__header__controls__container__icon" item xs={3}>
              <ListItemIcon>
                <RemoveCircleOutlineIcon
                  style={{
                    width: 'auto',
                    color: '#e8ca23',
                    fontSize: '1.1em'
                  }}
                />
              </ListItemIcon>
            </Grid>
            <Grid item xs={9}>
              <ListItemText primary={t(translations.pages.modules.isNotTakingPlaceButton())} />
            </Grid>
          </Grid>
        </MenuItem>
      ) : (
        <MenuItem className="module__header__contorls__container">
          <Grid
            container
            className="module__header__controls__container"
            onClick={e => {
              handleChangeIsTakingPlace();
              setMenuOpen(false);
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <Grid className="module__header__controls__container__icon" item xs={3}>
              <ListItemIcon>
                <CheckCircleOutlineIcon
                  style={{
                    width: 'auto',
                    color: 'green',
                    fontSize: '1.1em'
                  }}
                />
              </ListItemIcon>
            </Grid>
            <Grid item xs={9}>
              <ListItemText primary={t(translations.pages.modules.takesPlaceButton())} />
            </Grid>
          </Grid>
        </MenuItem>
      )}
      <MenuItem
        className="module__header__controls__container"
        disabled={selection ? hasAtLeastStarted(selection) : false}
      >
        <Grid
          container
          className="module__header__controls__container"
          onClick={e => {
            setDeleteModalOpen(true);
            setMenuOpen(false);
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <Grid className="module__header__controls__container__icon" item xs={3}>
            <ListItemIcon>
              <Icon
                className="far fa-trash-alt fa-sm "
                style={{
                  width: 'auto',
                  color: 'red',
                  fontSize: '1.1em'
                }}
              />
            </ListItemIcon>
          </Grid>
          <Grid item xs={9}>
            <ListItemText primary={t(translations.common.delete())} />
          </Grid>
        </Grid>
      </MenuItem>
    </ContextMenu>
  );
};

const checkState = (module: ModuleWithComplete) => {
  if (!module?.isTakingPlace && !module?.isDefault) {
    return States.IsNotTakingPlace;
  } else if (module?.complete) {
    return States.Complete;
  } else if (module?.isDefault) {
    return States.Draft;
  } else {
    return States.Incomplete;
  }
  // TODO use Flag to determine if the selection is finished
  // if (module?.isTakingPlace === true) {
  //   return States.TookPlace;
  // } else {
  //   return States.DidNotTakePlace;
  // }
};

interface CustomGridProps {
  content: string;
  children: React.ReactNode;
  className?: string;
  item: any;
  xs: number;
  component: any;
  elevation: number;
  onClick: () => void;
}

export const CustomGrid = (props: CustomGridProps) => {
  return <Grid {...props}>{props.children}</Grid>;
};

interface ContentProps {
  module;
  deactivateButtons;
  onClick;
  isPreview: boolean;
  setEditModalOpen;
  setDeleteModalOpen;
  setChangeStateModalOpen;
  handleChangeIsTakingPlace;
  setIsNotTakingPlaceModalOpen;
  inStudentView: boolean;
  checked: boolean;
  disabled: boolean;
  courses: Course[];
  selection?: ModuleSelection;
}

const Content = ({
  module,
  deactivateButtons,
  onClick,
  isPreview = false,
  setEditModalOpen,
  setDeleteModalOpen,
  setChangeStateModalOpen,
  handleChangeIsTakingPlace,
  setIsNotTakingPlaceModalOpen,
  inStudentView,
  checked,
  disabled,
  courses,
  selection
}: ContentProps) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const { t } = useTranslation();

  const anchorRef = useRef(null);

  return (
    <CustomGrid
      content={disabled ? t(translations.pages.events.cancelled()).toUpperCase() : ''}
      className={
        isPreview
          ? ''
          : disabled
          ? `module module__withHover-true module__withHover-cancelled module__activeState-${false}`
          : `module module__withHover-true module__activeState-${isModuleActive(module)}`
      }
      item
      xs={12}
      component={Paper}
      elevation={3}
      onClick={onClick}
    >
      {!inStudentView && (
        <Box className={`module__state module__state-${checkState(module)}`}>
          {t(translations.pages.modules[checkState(module)]())}
        </Box>
      )}
      <Grid container className="module__header">
        <Grid item container xs={9} className="module__header__name" alignItems="center">
          <Tooltip
            title={module.name || t(translations.pages.modules.create.modulePreview.title())}
          >
            <Typography>
              {module.name || t(translations.pages.modules.create.modulePreview.title())}
            </Typography>
          </Tooltip>
        </Grid>
        <Grid className="module__header__controls" item xs={3}>
          {!!deactivateButtons ? (
            disabled ? (
              <DoNotDisturbAltIcon color="error" />
            ) : (
              checked && <CheckCircleIcon color="success" />
            )
          ) : (
            <>
              <Fab
                className="action"
                onClick={e => {
                  setMenuOpen(true);
                  e.stopPropagation();
                  e.preventDefault();
                }}
                ref={anchorRef}
                style={{ zIndex: 222 }}
              >
                <Icon
                  className="fas fa-ellipsis-h fa-lg"
                  style={{
                    width: 'auto',
                    color: 'black'
                  }}
                />
              </Fab>
              <ControlButtons
                menuOpen={menuOpen}
                setMenuOpen={setMenuOpen}
                anchorRef={anchorRef}
                setEditModalOpen={setEditModalOpen}
                setDeleteModalOpen={setDeleteModalOpen}
                setChangeStateModalOpen={setChangeStateModalOpen}
                setIsNotTakingPlaceModalOpen={setIsNotTakingPlaceModalOpen}
                handleChangeIsTakingPlace={handleChangeIsTakingPlace}
                isTakingPlace={module.isTakingPlace}
                isActive={isModuleActive(module)}
                isDefault={module.isDefault}
                selection={selection}
              />
            </>
          )}
        </Grid>
      </Grid>
      <Divider />
      <Grid container className="module__body">
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={12} maxWidth={280}>
              <LabeledInput label={t(translations.pages.modules.lecturer()).toUpperCase()}>
                {getModuleLecturer(module, t).join(', ') || '-'}
              </LabeledInput>
            </Grid>
            <Grid item xs={12}>
              <LabeledInput label={t(translations.pages.modules.faculty()).toUpperCase()}>
                <Tooltip
                  title={
                    module.subjectType === 'FWPM'
                      ? courses.find(course => course.id === module.responsibleCourseOfStudy)?.name!
                      : t(translations.pages.modules.create.modulePreview.interdisciplinary())
                  }
                >
                  <div>
                    <ColouredInputBox
                      text={
                        module.subjectType === 'FWPM'
                          ? courses.find(course => course.id === module.responsibleCourseOfStudy)
                              ?.shorthand!
                          : 'FÜ'
                      }
                      color="#3C7900"
                      backgroundColor="#3C790031"
                    />
                  </div>
                </Tooltip>
              </LabeledInput>
            </Grid>
            {module.subjectType === 'AWPM' && (
              <Grid item xs={12}>
                <LabeledInput label={t(translations.pages.modules.grouping()).toUpperCase()}>
                  <ColouredInputBox
                    text={
                      module.grouping ? module.grouping.name : ''
                      // : groupingsData.getGroupings.find(
                      //     grouping => module.groupingId?.toString() === grouping.id // ? There is no groupingId Prop on a Module -> This never worked
                      //   )?.name
                    }
                    color="#48A2D1"
                    backgroundColor="#C1E6F7"
                  />
                </LabeledInput>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Divider />
      <Grid container className="module__footer" style={{ gap: 5 }}>
        <Grid container>
          <Grid
            container
            direction="row"
            alignItems="center"
            textAlign={'center'}
            style={{ gap: 10 }}
          >
            {t(translations.pages.modules.seatsAvailable()).toUpperCase()}
            <div
              style={{
                backgroundColor: isModuleActive(module) ? '#FFFFFF' : '#f2f2f2'
              }}
              className="module__footer__seats"
            >
              {module.maxParticipants}
            </div>
          </Grid>
        </Grid>
        <Grid container style={{ minHeight: 32 }} gap={1}>
          {module?.participantsConfigurations?.map((parConf, index) => {
            const checkExcluded = () => {
              if (parConf.isGlobal) {
                return 'FÜ';
              } else if (parConf.excludeCourses && parConf.excludedCourseOfStudyIds.length > 0)
                parConf.courseOfStudyIds = excludeCoursesFromAllCourses(
                  parConf.excludedCourseOfStudyIds,
                  courses
                );
              return parConf.courseOfStudyIds
                .map(id => courses.find(course => course.id === id)?.shorthand)
                .sort((a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1))
                .join(', ');
            };
            return <CourseChip key={index} shorthand={checkExcluded()} seats={parConf.seats} />;
          })}
        </Grid>
      </Grid>
    </CustomGrid>
  );
};

export function ModuleItem({
  module,
  deactivateButtons,
  onClick,
  onDelete,
  onEdit,
  subjectType,
  isPreview = false,
  deactiveLink = false,
  inStudentView = false,
  checked = false,
  disabled = false,
  selection
}: Props) {
  const { t } = useTranslation();
  const client = useApolloClient();
  const currentURL = useCurrentURL();
  const { currentUser } = client.readQuery({ query: USER_QUERY });
  const aclHelper = new ACLHelper(currentUser);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [changeStateModalOpen, setChangeStateModalOpen] = useState(false);
  const [isNotTakingPlaceModalOpen, setIsNotTakingPlaceModalOpen] = useState(false);

  const [deleteModule] = useMutation(MUTATION, {
    context: { clientName: 'federation' }
  });

  const [updateState] = useMutation(UPDATE_STATE, {
    context: { clientName: 'federation' }
  });

  const [updateTakingPlace] = useMutation(UPDATE_TAKING_PLACE, {
    context: { clientName: 'federation' }
  });

  const { data: courses } = useCoursesQuery();

  const isSelectionAdmin = aclHelper.isSelectionAdmin();
  const isAWMPSelectionAdmin = aclHelper.isSubjectSelectionAdmin(false, SubjectType.Awpm);

  function isAllowedToEdit() {
    if (deactivateButtons) return false;

    if (aclHelper.isSuperUser()) {
      return true;
    }

    if (!isSelectionAdmin) {
      return false;
    }
    if (subjectType === SubjectType.Fwpm) {
      const courseOfModule = courses.find(course => course.id === module.responsibleCourseOfStudy);
      if (!courseOfModule) {
        return false;
      }
      const isSelectionAdminOfCourse = aclHelper.isSelectionAdminForCourseOfStudy(
        courseOfModule?.faculty?.shorthand
      );
      if (isSelectionAdminOfCourse) {
        return true;
      }
    } else if (subjectType === SubjectType.Awpm && isAWMPSelectionAdmin) {
      return true;
    }
  }

  let allowedToEdit = isAllowedToEdit();

  const overrodeDeactivateButtons = deactivateButtons || !allowedToEdit;

  const dispatch = useDispatch();
  const handleDelete = async () => {
    const { errors } = await deleteModule({
      variables: {
        id: Number(module.id)
      },
      refetchQueries: [DEFAULT_MODULES_QUERY]
    });
    if (errors) {
      dispatch(AlertActions.error(t(translations.pages.modules.alerts.deleteFailed())));
    } else {
      dispatch(AlertActions.success(t(translations.pages.modules.alerts.deleteSuccessful())));
      onDelete?.();
    }
  };

  const queryClient = useQueryClient();

  const handleChangeIsTakingPlace = async () => {
    const { errors } = await updateTakingPlace({
      variables: {
        id: Number(module.id),
        state: !module.isTakingPlace
      }
    });
    queryClient.invalidateQueries({ queryKey: ['modules'] });
    if (errors) {
      dispatch(AlertActions.error(t(translations.pages.modules.alerts.changeStateFailed())));
    } else {
      dispatch(AlertActions.success(t(translations.pages.modules.alerts.changeStateSuccssful())));
    }
  };

  const handleChangeState = async () => {
    const { errors } = await updateState({
      variables: {
        id: Number(module.id),
        data: {
          activeState: isModuleActive(module) ? ActiveState.Inactive : ActiveState.Active
        }
      },
      refetchQueries: [DEFAULT_MODULES_QUERY]
    });
    if (errors) {
      dispatch(AlertActions.error(t(translations.pages.modules.alerts.changeStateFailed())));
    } else {
      dispatch(AlertActions.success(t(translations.pages.modules.alerts.changeStateSuccssful())));
      onEdit?.();
    }
  };

  return (
    <>
      <ErrorBoundary boundaryTag="moduleDetailBoundary">
        <Modal
          open={editModalOpen}
          onClose={(_event, reason) => {
            if (reason && reason === 'backdropClick') return;
            setCancelModalOpen(true);
          }}
          headline={t(
            translations.pages.modules[!module?.id ? 'create' : 'edit'].title[
              subjectType.toLowerCase()
            ]()
          )}
          disabledCancel={disabledCancel}
          onCancel={() => {
            setDisabledCancel(true);
            setCancelModalOpen(true);
            setDisabledCancel(false);
          }}
        >
          <ModuleModal
            module={module}
            closeHandler={() => {
              setEditModalOpen(false);
              onEdit && onEdit();
            }}
            setDisabledCancel={setDisabledCancel}
            subjectType={subjectType}
            courses={courses}
          />
        </Modal>
      </ErrorBoundary>
      {isPreview ? (
        <Content
          module={module}
          deactivateButtons={overrodeDeactivateButtons}
          isPreview={isPreview}
          onClick={onClick}
          setEditModalOpen={setEditModalOpen}
          setDeleteModalOpen={setDeleteModalOpen}
          setChangeStateModalOpen={setChangeStateModalOpen}
          setIsNotTakingPlaceModalOpen={setIsNotTakingPlaceModalOpen}
          handleChangeIsTakingPlace={handleChangeIsTakingPlace}
          inStudentView={inStudentView}
          checked={checked}
          disabled={disabled}
          courses={courses}
          selection={selection}
        />
      ) : (
        <Link
          to={!deactiveLink ? `/${subjectType.toLowerCase()}/modules/${module.id}` : ''}
          state={{ back: currentURL }}
        >
          <Content
            module={module}
            deactivateButtons={overrodeDeactivateButtons}
            isPreview={isPreview}
            onClick={onClick}
            setEditModalOpen={setEditModalOpen}
            setDeleteModalOpen={setDeleteModalOpen}
            setChangeStateModalOpen={setChangeStateModalOpen}
            setIsNotTakingPlaceModalOpen={setIsNotTakingPlaceModalOpen}
            handleChangeIsTakingPlace={handleChangeIsTakingPlace}
            inStudentView={inStudentView}
            disabled={disabled}
            checked={checked}
            courses={courses}
            selection={selection}
          />
        </Link>
      )}
      <ConfirmationDialog
        open={isNotTakingPlaceModalOpen}
        title={t(translations.pages.modules.isNotTakingPlaceDialogTitle())}
        onClose={() => {
          setIsNotTakingPlaceModalOpen(false);
        }}
        onConfirm={() => {
          setIsNotTakingPlaceModalOpen(false);
          handleChangeIsTakingPlace();
        }}
      >
        <div>{t(translations.pages.modules.isNotTakingPlaceDialoginfo())}</div>
      </ConfirmationDialog>
      <ConfirmationDialog
        open={deleteModalOpen}
        title={t(translations.pages.modules.deleteConfirm.title())}
        onClose={() => {
          setDeleteModalOpen(false);
        }}
        onConfirm={() => {
          setDeleteModalOpen(false);
          handleDelete();
        }}
      >
        <div>{t(translations.pages.modules.deleteConfirm.message())}</div>
      </ConfirmationDialog>
      <ConfirmationDialog
        open={cancelModalOpen}
        title={t(translations.common.cancelConfirm.title())}
        onClose={() => {
          setCancelModalOpen(false);
        }}
        onConfirm={() => {
          setEditModalOpen(false);
          setCancelModalOpen(false);
        }}
      >
        <div>{t(translations.common.cancelConfirm.message())}</div>
      </ConfirmationDialog>
      <ConfirmationDialog
        open={changeStateModalOpen}
        title={
          isModuleActive(module)
            ? t(translations.pages.modules.setStateConfirm.setInavctiveTitle())
            : t(translations.pages.modules.setStateConfirm.setActiveTitle())
        }
        onClose={() => {
          setChangeStateModalOpen(false);
        }}
        onConfirm={() => {
          setChangeStateModalOpen(false);
          handleChangeState();
        }}
      >
        <div>
          {isModuleActive(module)
            ? t(translations.pages.modules.setStateConfirm.setInactive())
            : t(translations.pages.modules.setStateConfirm.setActive())}
        </div>
      </ConfirmationDialog>
    </>
  );
}
